import { DefaultRouteTabs, SaveButton } from '@/components/_common';
import {
  IntermediaryIndicationType,
  InvoiceGeneralFormValues,
} from '@/core/domain/invoice/invoice/Invoice.types';

import { PageProps } from '@/types/PageProps';
import { Divider, Form } from 'antd';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { useEffect } from 'react';
import { useInsurerMutation } from '@/core/domain/people/Insurers/Insurers.hooks';
import { useCustomerReduced } from '@/core/domain/people/Customers/Customers.hooks';
import { useSupplierReducedMutation } from '@/core/domain/people/Suppliers/Suppliers.hooks';
import RecipientForm from '@/components/Fiscal/Invoice/InvoiceForms/InvoiceRecipientForm/RecipientForm';
import { useInvoiceFormContext } from '@/components/Fiscal/Invoice/InvoiceFormProvider';
import { useDefaultFormErrorNotification } from '@/hooks';
import { InvoiceRecipientType } from '@/core/domain/invoice/invoices/invoices.enums';
import InvoiceButtonsRepeated from '../InvoiceButtonsRepeated';
import invoiceValidateRoutes from '../../Invoice.validateRoutes';
import { useGetCustomerMainAddress } from '@/core/domain/people/CustomerAddresses/CustomerAddresses.hooks';
import { useGetSupplierMainAddress } from '@/core/domain/people/SupplierAddress/SupplierAddress.hooks';
import { useGetInsurerMainAddress } from '@/core/domain/people/InsurerAddress/InsurersAddress.hooks';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import InvoiceDeleteButton from '@/components/_common/InvoiceDeleteButton';

interface Entity {
  name: string;
  cnpj?: string;
  cpf?: string;
  cpfCnpj?: string;
  foreignDocument?: string;
  fantasyName?: string;
  customerAddress?: string;
  cityName?: string;
  isPublicAgency?: boolean;
}

interface Address {
  street: string;
  number?: string;
  district: string;
  city: string;
}

const InvoiceRecipientPage: React.FC<PageProps> = ({ route }) => {
  const intl = useIntl().formatMessage;

  const {
    forms,
    instanceForms,
    invoiceId,
    disabled,
    disabledFields,
    invoice: { data: invoice, isLoading },
    handleSubmit,
  } = useInvoiceFormContext();

  const [form] = Form.useForm<InvoiceGeneralFormValues>(
    instanceForms.invoiceRecipient,
  );

  const {
    parent: { key },
  } = route.parent;

  const customerReduced = useCustomerReduced();
  const insurerMutation = useInsurerMutation();
  const supplierReduced = useSupplierReducedMutation();

  const customerMainAddress = useGetCustomerMainAddress();
  const supplierMainAddress = useGetSupplierMainAddress();
  const insurerMainAddress = useGetInsurerMainAddress();

  const { sendFormErrorNotification } = useDefaultFormErrorNotification();

  // const invoiceEditNFe = useInvoiceEditNFe();
  // const invoiceEditNFc = useInvoiceEditNFc();
  // const invoiceEditNFs = useInvoiceEditNFs();

  function createRecipientData(entity: Entity, address?: Address) {
    const fullAddress = `${address?.street ? address.street + ',' : ''} ${
      address?.number ?? ''
    } - ${address?.district ?? ''}`;
    return {
      name: entity.name,
      cpfCnpj: entity.cpfCnpj || entity.cnpj || entity.cpf,
      foreignDocument: entity.foreignDocument,
      fantasyName: entity.fantasyName,
      cityName: address?.city,
      customerAddress: address ? fullAddress : undefined,
    };
  }

  async function getCustomerData(customerId: number) {
    let customer, address;

    try {
      customer = await customerReduced.mutateAsync(customerId);
      address = await customerMainAddress.mutateAsync(customerId);
    } catch {}

    if (customer) return createRecipientData(customer, address);
  }

  async function getInsurerData(insurerId: number) {
    let insurer, address;

    try {
      insurer = await insurerMutation.mutateAsync(insurerId);
      address = await insurerMainAddress.mutateAsync(insurerId);
    } catch {}

    if (insurer) return createRecipientData(insurer, address);
  }

  async function getSupplierData(supplierId: number) {
    let supplier, address;

    try {
      supplier = await supplierReduced.mutateAsync(supplierId);
      address = await supplierMainAddress.mutateAsync(supplierId);
    } catch {}

    if (supplier) return createRecipientData(supplier, address);
  }

  useEffect(() => {
    async function getValuesInputs() {
      if (invoice) {
        let recipientType;

        let data: Entity | undefined;

        if (invoice.customerId) {
          recipientType = InvoiceRecipientType.CUSTOMER;
          data = await getCustomerData(invoice.customerId);
        }

        if (invoice.insurerId) {
          recipientType = InvoiceRecipientType.INSURER;
          data = await getInsurerData(invoice.insurerId);
        }

        if (invoice.supplierId) {
          recipientType = InvoiceRecipientType.SUPPLIER;
          data = await getSupplierData(invoice.supplierId);
        }

        form.setFieldsValue({
          intermediaryIndication:
            Number(invoice.intermediaryIndication) === 1 ? 'true' : 'false',
          intermediaryIndicationNfs:
            Number(invoice.intermediaryIndication) === 1 ? true : false,

          intermediaryCpfCnpj: invoice.intermediaryCpfCnpj,
          registrationIdentifier: invoice.registrationIdentifier,
          recipient: {
            cpfCnpj: data?.cpfCnpj,
            foreignDocument: data?.foreignDocument,
            recipientType: recipientType,
            customer: data?.name,
            supplier: data?.name,
            insurer: data?.name,
            fantasyName: data?.fantasyName,
            customerAddress: data?.customerAddress,
            cityName: data?.cityName,
            customerId: invoice.customerId,
            supplierId: invoice.supplierId,
            insurerId: invoice.insurerId,
            taxpayerType: invoice.taxpayerType,
            publicAgency:
              typeof invoice?.publicAgency === 'boolean'
                ? String(invoice.publicAgency)
                : undefined,
            intermediaryIndicationType:
              invoice?.intermediaryCpfCnpj &&
              invoice?.intermediaryCpfCnpj.length > 11
                ? IntermediaryIndicationType.cnpj
                : IntermediaryIndicationType.cpf,
            intermediaryCityCode: invoice?.intermediaryCityCode,
            intermediaryCompanyName: invoice?.intermediaryCompanyName,
            intermediaryCpfCnpj: invoice?.intermediaryCpfCnpj,
            intermediaryMunicipalRegistration:
              invoice?.intermediaryMunicipalRegistration,
          },
        });
      }
    }

    if (forms.invoiceRecipient) {
      form.setFieldsValue(forms.invoiceRecipient);
    } else if (invoice) {
      getValuesInputs();
    }
  }, [invoice]);

  async function onHandleSubmit() {
    try {
      await handleSubmit();
    } catch (error: unknown) {
      sendFormErrorNotification(error);
    }
  }

  return (
    <DefaultRouteTabs
      baseKey={invoiceId}
      permissions={route.permissions}
      basePath={`/app/fiscal/invoice/${key}/${invoiceId}`}
      route={invoiceValidateRoutes(route, invoice?.situation)}
      resourceNotFound={!invoice && !isLoading}
      extra={
        <SpaceForButton>
          <InvoiceDeleteButton />

          <SaveButton
            block
            type="primary"
            loading={isLoading}
            onClick={onHandleSubmit}
            disabled={disabled || disabledFields}
          >
            {intl({ id: 'commons.entity.save' })}
          </SaveButton>
        </SpaceForButton>
      }
    >
      <Helmet title={intl({ id: route.name })} />

      <RecipientForm
        form={form}
        isEdit={invoice ? true : false}
        selecteInvoiceType={invoice?.invoiceType}
      />

      <Divider />

      <InvoiceButtonsRepeated />
    </DefaultRouteTabs>
  );
};

export default InvoiceRecipientPage;
